import React, { useCallback, useRef, useState } from "react"
import { PlusIcon, CheckIcon, PencilIcon } from "@heroicons/react/24/outline"
import Chip from "~/ui-rtk/components/ui/common/Chip"
import {
  filterFreeTextTagInput,
  formatTagLabel,
  isPendingFreeTextId,
  pendingFreeTextId,
  pendingFreeTextLabel,
  sanitizeFreeTextTagValue,
  TAG_RESERVED_SEPARATOR_HINT,
  type TagCategory,
  type TagOption,
} from "~/ui-rtk/types/structured-tags"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

const TAG_COLOR_BG: Record<string, string> = {
  red: "bg-system-error-100 text-system-error-800 border-system-error-200",
  orange:
    "bg-system-orange-100 text-system-orange-800 border-system-orange-200",
  yellow:
    "bg-system-yellow-100 text-system-yellow-800 border-system-yellow-200",
  green:
    "bg-system-success-100 text-system-success-800 border-system-success-200",
  blue: "bg-system-blue-100 text-system-blue-800 border-system-blue-200",
  indigo: "bg-system-sea-100 text-system-sea-800 border-system-sea-200",
  purple:
    "bg-system-purple-100 text-system-purple-800 border-system-purple-200",
  pink: "bg-system-brand-pink-100 text-system-brand-pink-800 border-system-brand-pink-200",
  gray: "bg-system-gray-100 text-system-gray-800 border-system-gray-200",
}

interface TagCategoryRowProps {
  category: TagCategory
  selectedOptionIds: string[]
  onChange: (optionIds: string[]) => void
  onCreateOption?: (label: string) => Promise<TagOption>
  /** Rename this category's display label (alias). Slug is preserved server-side. */
  onRename?: (label: string) => Promise<void>
  /**
   * Render the values as static pills instead of selectable buttons, for places
   * that manage the taxonomy rather than tag an item (Company Settings → Ad
   * naming). Rename and "Add value" still work; only picking is disabled, so a
   * pill can't look clickable when nothing would be saved.
   */
  readOnlySelection?: boolean
  /**
   * free_text only: keep typed values purely local instead of calling
   * onCreateOption immediately. The value is selected under a pending
   * `text:<label>` id; the owner (TagPanel) resolves pending ids at save time
   * via `freeTextTags`, so discarding the dialog persists nothing.
   */
  deferFreeTextCreation?: boolean
}

export const TagCategoryRow: React.FC<TagCategoryRowProps> = ({
  category,
  selectedOptionIds,
  onChange,
  onCreateOption,
  onRename,
  readOnlySelection = false,
  deferFreeTextCreation = false,
}) => {
  const [isCreating, setIsCreating] = useState(false)
  const [newLabel, setNewLabel] = useState("")
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState(category.label)
  // Idempotency guard: pressing Enter runs handleRename, then unmounting the
  // input can fire onBlur -> handleRename again before the optimistic label
  // update propagates (so the `next === category.label` check can't catch it).
  // The ref is read synchronously, so the second call exits without a duplicate
  // PATCH.
  const renameFired = useRef(false)

  const handleRename = useCallback(async () => {
    if (renameFired.current) return
    const next = renameValue.trim()
    setIsRenaming(false)
    if (!onRename || !next || next === category.label) return
    renameFired.current = true
    try {
      await onRename(next)
    } finally {
      renameFired.current = false
    }
  }, [renameValue, onRename, category.label])
  // Track options created in this session (not yet in cached category.options)
  const [localOptions, setLocalOptions] = useState<TagOption[]>([])
  // free_text only: a reserved separator character was just stripped from input
  const [strippedSeparator, setStrippedSeparator] = useState(false)

  const isMulti = category.allowMultiple === true
  const isFreeText = category.valueType === "free_text"

  // Merge category options with locally created ones
  const allOptions = [
    ...category.options,
    ...localOptions.filter(lo => !category.options.find(o => o.id === lo.id)),
  ]

  const handleOptionClick = useCallback(
    (optionId: string) => {
      const isSelected = selectedOptionIds.includes(optionId)

      if (isMulti) {
        // Multi-value: toggle on/off
        if (isSelected) {
          onChange(selectedOptionIds.filter(id => id !== optionId))
        } else {
          onChange([...selectedOptionIds, optionId])
        }
      } else {
        // Single-value: deselect if already selected, otherwise replace
        if (isSelected) {
          onChange([])
        } else {
          onChange([optionId])
        }
      }
    },
    [isMulti, selectedOptionIds, onChange],
  )

  const handleCreate = useCallback(async () => {
    const label = isFreeText
      ? sanitizeFreeTextTagValue(newLabel)
      : newLabel.trim()
    if (!label) return

    // Deferred free text: select a pending `text:<label>` id and persist
    // nothing — the owner resolves it at save time.
    if (isFreeText && deferFreeTextCreation) {
      const id = pendingFreeTextId(label)
      if (isMulti) {
        onChange(
          selectedOptionIds.includes(id)
            ? selectedOptionIds
            : [...selectedOptionIds, id],
        )
      } else {
        onChange([id])
      }
      setNewLabel("")
      setStrippedSeparator(false)
      return
    }

    if (!onCreateOption) return
    const option = await onCreateOption(label)
    setLocalOptions(prev => [...prev, option])
    if (isMulti) {
      onChange(
        selectedOptionIds.includes(option.id)
          ? selectedOptionIds
          : [...selectedOptionIds, option.id],
      )
    } else {
      onChange([option.id])
    }
    setNewLabel("")
    setStrippedSeparator(false)
    setIsCreating(false)
  }, [
    newLabel,
    isFreeText,
    deferFreeTextCreation,
    onCreateOption,
    isMulti,
    selectedOptionIds,
    onChange,
  ])

  /**
   * free_text input filter: reserved separator chars never enter the field.
   * The warning is sticky while typing (a fast typist would otherwise never
   * see it) and resets when the field is emptied or the value is submitted.
   */
  const handleFreeTextChange = useCallback((raw: string) => {
    const filtered = filterFreeTextTagInput(raw)
    if (filtered !== raw) {
      setStrippedSeparator(true)
    } else if (!filtered) {
      setStrippedSeparator(false)
    }
    setNewLabel(filtered)
  }, [])

  return (
    <div className="py-3 border-b border-system-blue-600/20 last:border-b-0">
      <div className="flex items-start gap-3">
        {/* Category label — single row: name + (multi/single). Label is
            user-authored (system defaults are already pretty, aliases are typed
            verbatim), so it's rendered as-is rather than re-prettified. */}
        <div className="w-44 flex-shrink-0 pt-1 flex items-center gap-1.5 whitespace-nowrap">
          {isRenaming ? (
            <input
              type="text"
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onBlur={() => void handleRename()}
              onKeyDown={e => {
                if (e.key === "Enter") void handleRename()
                if (e.key === "Escape") {
                  setRenameValue(category.label)
                  setIsRenaming(false)
                }
              }}
              className="px-2 py-1 text-xs border border-system-blue-500/30 rounded bg-system-blue-600/10 text-white focus:outline-none focus:ring-1 focus:ring-system-blue-400/50 w-28"
              autoFocus
            />
          ) : (
            <span className="text-xs font-semibold text-system-gray-300 flex items-center gap-1 group">
              {category.label}
              {onRename && (
                <button
                  type="button"
                  aria-label={`Rename ${category.label}`}
                  onClick={() => {
                    setRenameValue(category.label)
                    setIsRenaming(true)
                  }}
                  className="opacity-0 group-hover:opacity-100 text-system-gray-500 hover:text-system-gray-300 transition-opacity cursor-pointer"
                >
                  <PencilIcon className="w-3 h-3" />
                </button>
              )}
            </span>
          )}
          <span className="text-[10px] text-system-gray-500">
            {isFreeText ? "(free text)" : isMulti ? "(multi)" : "(single)"}
          </span>
        </div>

        {/* Options */}
        <div className="flex-1 flex flex-wrap gap-1.5 items-center">
          {/* free_text: no option pool — selected value(s) as chips + a typing
              field. Reserved separator chars are filtered on input AND
              sanitized on submit (the API strips them again server-side). */}
          {isFreeText && (
            <>
              {selectedOptionIds.map(id => {
                const opt = allOptions.find(o => o.id === id)
                const label = opt
                  ? opt.label
                  : isPendingFreeTextId(id)
                    ? pendingFreeTextLabel(id)
                    : id
                return (
                  <Chip
                    key={id}
                    className="px-2 py-0.5 text-xs bg-system-blue-100 text-system-blue-800 border border-system-blue-200"
                    onRemove={
                      readOnlySelection
                        ? undefined
                        : () =>
                            onChange(
                              selectedOptionIds.filter(oid => oid !== id),
                            )
                    }
                  >
                    {label}
                  </Chip>
                )
              })}
              {readOnlySelection ? (
                <span className="text-xs italic text-system-gray-500">
                  Free text — values are typed when tagging
                </span>
              ) : (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1.5">
                    <input
                      type="text"
                      value={newLabel}
                      onChange={e => handleFreeTextChange(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter") void handleCreate()
                        if (e.key === "Escape") {
                          setNewLabel("")
                          setStrippedSeparator(false)
                        }
                      }}
                      placeholder={
                        isMulti || selectedOptionIds.length === 0
                          ? "Type value…"
                          : "Type to replace…"
                      }
                      className="px-2 py-1 text-xs border border-system-blue-500/30 rounded bg-system-blue-600/10 text-white placeholder-system-gray-500 focus:outline-none focus:ring-1 focus:ring-system-blue-400/50 w-40"
                    />
                    {newLabel.trim() && (
                      <button
                        onClick={() => void handleCreate()}
                        className="px-2 py-1 text-xs bg-system-brand-pink-500 text-white rounded hover:bg-system-brand-pink-600 transition-colors"
                      >
                        {isMulti || selectedOptionIds.length === 0
                          ? "Add"
                          : "Replace"}
                      </button>
                    )}
                  </div>
                  {strippedSeparator && (
                    <span className="text-[10px] text-system-yellow-400">
                      Separator characters ({TAG_RESERVED_SEPARATOR_HINT}) are
                      reserved for ad names and were removed
                    </span>
                  )}
                </div>
              )}
            </>
          )}

          {!isFreeText &&
            allOptions.map(opt => {
              const isSelected = selectedOptionIds.includes(opt.id)
              if (readOnlySelection) {
                return (
                  <span
                    key={opt.id}
                    className="px-2.5 py-1 rounded-lg text-xs font-medium border bg-system-blue-600/10 text-system-gray-300 border-system-blue-600/20"
                  >
                    {formatTagLabel(opt.label)}
                  </span>
                )
              }
              return (
                <button
                  key={opt.id}
                  onClick={() => handleOptionClick(opt.id)}
                  className={cn(
                    "px-2.5 py-1 rounded-lg text-xs font-medium border transition-all duration-150 cursor-pointer flex items-center gap-1",
                    isSelected
                      ? TAG_COLOR_BG[opt.color] || TAG_COLOR_BG.blue
                      : "bg-system-blue-600/10 text-system-gray-400 border-system-blue-600/20 hover:border-system-blue-500/40 hover:text-system-gray-300",
                  )}
                >
                  {isSelected && <CheckIcon className="w-3 h-3" />}
                  {formatTagLabel(opt.label)}
                </button>
              )
            })}

          {/* Selected chips for options not found in allOptions (shouldn't happen normally) */}
          {!isFreeText &&
            selectedOptionIds
              .filter(id => !allOptions.find(o => o.id === id))
              .map(id => (
                <Chip
                  key={id}
                  className="px-2 py-0.5 text-xs bg-system-blue-100 text-system-blue-800 border border-system-blue-200"
                  onRemove={() =>
                    onChange(selectedOptionIds.filter(oid => oid !== id))
                  }
                >
                  {id}
                </Chip>
              ))}

          {/* Add custom value button. Predefined categories accept new values
              too — adding one extends the predefined list (matches the API,
              which no longer blocks it). free_text uses its own persistent
              input above instead. */}
          {!isFreeText && !isCreating && (
            <button
              onClick={() => setIsCreating(true)}
              className="flex items-center gap-1 px-2 py-1 text-xs text-system-blue-400 hover:text-system-blue-300 border border-dashed border-system-blue-500/30 rounded-lg transition-colors cursor-pointer"
            >
              <PlusIcon className="w-3 h-3" />
              Add
            </button>
          )}

          {/* Inline create form */}
          {!isFreeText && isCreating && (
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") void handleCreate()
                  if (e.key === "Escape") {
                    setIsCreating(false)
                    setNewLabel("")
                  }
                }}
                placeholder="Type value..."
                className="px-2 py-1 text-xs border border-system-blue-500/30 rounded bg-system-blue-600/10 text-white placeholder-system-gray-500 focus:outline-none focus:ring-1 focus:ring-system-blue-400/50 w-28"
                autoFocus
              />
              <button
                onClick={() => void handleCreate()}
                disabled={!newLabel.trim()}
                className="px-2 py-1 text-xs bg-system-brand-pink-500 text-white rounded hover:bg-system-brand-pink-600 disabled:opacity-50 transition-colors"
              >
                Add
              </button>
              <button
                onClick={() => {
                  setIsCreating(false)
                  setNewLabel("")
                }}
                className="px-2 py-1 text-xs text-system-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
